/* eslint-disable @typescript-eslint/no-explicit-any */
import { z, type ZodType } from "zod";

import {
  type EnsureItemNotInArray,
  type EnsureItemsInArray,
  type IsEmptyArray,
  type Prettify,
  type RemoveItemsFromArray,
} from "@thalamos/common";

import {
  buildTranslationInput,
  createPermissiveSchema,
  tk,
  type BaseFormBuilderField,
  type FormBuilderField,
  type FormBuilderReadonlyField,
  type IncidentWorkItemWithContext,
} from "../types/index.js";
import { translate, type TFunction } from "../types/Translation.js";

type FieldInfoBaseInput<
  FieldName extends string,
  FieldSchema extends z.ZodType,
> = FormBuilderField<any, FieldName> & {
  schema: FieldSchema;
};

type FieldNeverRequiredInput<
  FieldName extends string,
  FieldSchema extends z.ZodType,
> = Omit<
  FieldInfoBaseInput<FieldName, FieldSchema> & {
    defaultValue?: ({
      incidentWorkItem,
    }: {
      incidentWorkItem?: IncidentWorkItemWithContext;
    }) => z.infer<FieldSchema> | null;
  },
  "type"
>;

type FieldAlwaysRequiredInput<
  FieldName extends string,
  FieldSchema extends z.ZodType,
  FormSchema extends Record<string, unknown>,
> = FieldInfoBaseInput<FieldName, FieldSchema> & {
  defaultValue?: ({
    incidentWorkItem,
  }: {
    incidentWorkItem?: IncidentWorkItemWithContext;
  }) => z.infer<FieldSchema> | null;
  isEditable?: ({
    initialValue,
    formValues,
  }: {
    initialValue: z.infer<FieldSchema> | null;
    formValues: Partial<FormSchema>;
  }) => boolean;
};

type FieldConditionallyRequiredInput<
  FieldName extends string,
  FieldSchema extends z.ZodType,
  FormSchema extends Record<string, unknown>,
> = FieldInfoBaseInput<FieldName, FieldSchema> & {
  defaultValue?: ({
    incidentWorkItem,
  }: {
    incidentWorkItem?: IncidentWorkItemWithContext;
  }) => z.infer<FieldSchema> | null;
  isVisible: ({
    formValues,
    incidentWorkItem,
  }: {
    formValues: Partial<FormSchema>;
    incidentWorkItem?: IncidentWorkItemWithContext;
  }) => boolean;
  isEditable?: ({
    initialValue,
    formValues,
  }: {
    initialValue: z.infer<FieldSchema> | null;
    formValues: Partial<FormSchema>;
  }) => boolean;
};

type ReadonlyFieldInfoBaseInput<
  FieldName extends string,
  FormSchema extends Record<string, unknown>,
> = FormBuilderReadonlyField<FormSchema, FieldName>;

type ReadonlyFieldAlwaysRequiredInput<
  FieldName extends string,
  FormSchema extends Record<string, unknown>,
> = ReadonlyFieldInfoBaseInput<FieldName, FormSchema>;

type ReadonlyFieldConditionallyRequiredInput<
  FieldName extends string,
  FormSchema extends Record<string, unknown>,
> = ReadonlyFieldInfoBaseInput<FieldName, FormSchema> & {
  isVisible: ({
    formValues,
    incidentWorkItem,
  }: {
    formValues: Partial<FormSchema>;
    incidentWorkItem?: IncidentWorkItemWithContext;
  }) => boolean;
};

type FieldInfo<
  FieldName extends Readonly<string>,
  FieldSchema extends z.ZodType,
  FormSchema extends Record<string, unknown>,
> = FieldInfoBaseInput<FieldName, FieldSchema> & {
  defaultValue?: ({
    incidentWorkItem,
  }: {
    incidentWorkItem?: IncidentWorkItemWithContext;
  }) => z.infer<FieldSchema> | null;
  isVisible:
    | boolean
    | FieldConditionallyRequiredInput<
        FieldName,
        FieldSchema,
        FormSchema
      >["isVisible"];
  isEditable?:
    | boolean
    | FieldConditionallyRequiredInput<
        FieldName,
        FieldSchema,
        FormSchema
      >["isEditable"];
};

type ReadonlyFieldInfo<
  FieldName extends string,
  FormSchema extends Record<string, unknown>,
> = ReadonlyFieldInfoBaseInput<FieldName, FormSchema> & {
  isVisible:
    | true
    | ReadonlyFieldConditionallyRequiredInput<
        FieldName,
        FormSchema
      >["isVisible"];
};

/**
 * A form validation issue.
 */
type FormValidationIssue = { fieldName: string; message: string };

/**
 * A form validation function.
 */
type FormValidationFn<FormValues extends Record<string, unknown>> = (args: {
  t: TFunction;
  formValues: FormValues;
  incidentWorkItem?: IncidentWorkItemWithContext;
}) => FormValidationIssue[];

type PageInfoInput<FieldNames extends readonly unknown[]> = {
  fields: readonly FieldNames[number][];
};

type PageInfoOutput = {
  fields: readonly FormBuilderField<any, any>[];
};

/**
 * Transforms an object so that all values which are invalid are set to undefined.
 * This is useful during the process of the form being filled out, as it allows us
 * to treat fields with intermediate values as undefined.
 */
export function treatInvalidValuesAsUndefined<Schema extends z.ZodType<any>>(
  strictFieldSchemas: Record<string, ZodType<any>>,
  values: z.infer<Schema>,
): Partial<z.infer<Schema>> {
  const valuesWithInvalidValuesSetToUndefined = Object.fromEntries(
    Object.keys(values).map((key) => {
      const parsedResult = (
        strictFieldSchemas[key] as ZodType<any> | undefined
      )?.safeParse(values[key]);
      return [key, parsedResult?.success ? parsedResult.data : undefined];
    }),
  );

  // This line is useful for debugging. It should only output a valid value
  // as reflected in the TypeScript types, or undefined. It should never output
  // partially completed field information.
  // console.log(valuesWithInvalidValuesSetToUndefined);

  return valuesWithInvalidValuesSetToUndefined as Partial<z.infer<Schema>>;
}

export class MultiPageFormBuilder<
  FieldNames extends string[] = [],
  UnallocatedFieldNames extends string[] = [],
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  FieldSchemas extends Record<string, z.ZodType<any>> = {},
> {
  private fields: FieldInfo<any, z.ZodType<any>, any>[];

  private readonlyFields: ReadonlyFieldInfo<any, any>[];

  private pages: PageInfoInput<any>[];
  private validations: FormValidationFn<Record<string, unknown>>[];

  constructor() {
    this.fields = [];
    this.readonlyFields = [];
    this.pages = [];
    this.validations = [];
  }

  /**
   * Declare the existence of a field in the form.
   * The field is not required to be shown on any page
   * amd will never be visible.
   *
   * @param field Information about the field
   * @returns
   */
  withFieldNeverVisible<
    FieldName extends string,
    FieldSchema extends z.ZodType,
  >(
    field: FieldNeverRequiredInput<
      EnsureItemNotInArray<FieldName, FieldNames>,
      FieldSchema
    >,
  ): MultiPageFormBuilder<
    [...FieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    UnallocatedFieldNames,
    Prettify<FieldSchemas & { [K in FieldName]: z.infer<FieldSchema> }>
  > {
    this.fields.push({
      ...field,
      isVisible: false,
      isReadOnly: true,
      type: "never",
    });

    return this as never;
  }

  /**
   * Declare the existence of a field in the form.
   * The field is always required.
   *
   * @param field Information about the field
   * @returns
   */
  withFieldAlwaysVisible<
    FieldName extends string,
    FieldSchema extends z.ZodType,
  >(
    field: FieldAlwaysRequiredInput<
      EnsureItemNotInArray<FieldName, FieldNames>,
      FieldSchema,
      FieldSchemas
    >,
  ): MultiPageFormBuilder<
    [...FieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    [...UnallocatedFieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    Prettify<FieldSchemas & { [K in FieldName]: z.infer<FieldSchema> }>
  > {
    this.fields.push({ ...field, isVisible: true, isReadOnly: false });

    return this as never;
  }

  /**
   * Declare the existence of a field in the form.
   * The field is conditionally required so may be null.
   *
   * @param field Information about the field
   * @returns
   */
  withFieldConditionallyVisible<
    FieldName extends string,
    FieldSchema extends z.ZodType,
  >(
    field: FieldConditionallyRequiredInput<
      EnsureItemNotInArray<FieldName, FieldNames>,
      FieldSchema,
      FieldSchemas
    >,
  ): MultiPageFormBuilder<
    [...FieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    [...UnallocatedFieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    Prettify<FieldSchemas & { [K in FieldName]: z.infer<FieldSchema> | null }>
  > {
    this.fields.push(field);

    return this as never;
  }

  /**
   * Declare the existence of a readonly field in the form.
   * The field is always visible.
   *
   * @param field Information about the field
   * @returns
   */
  withReadonlyFieldAlwaysVisible<FieldName extends string>(
    field: ReadonlyFieldAlwaysRequiredInput<
      EnsureItemNotInArray<FieldName, FieldNames>,
      FieldSchemas
    >,
  ): MultiPageFormBuilder<
    [...FieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    [...UnallocatedFieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    FieldSchemas
  > {
    this.readonlyFields.push({ ...field, isVisible: true, isReadOnly: true });

    return this as never;
  }

  /**
   * Declare the existence of a field in the form.
   * The field is conditionally required so may be null.
   *
   * @param field Information about the field
   * @returns
   */
  withReadonlyFieldConditionallyVisible<FieldName extends string>(
    field: ReadonlyFieldConditionallyRequiredInput<
      EnsureItemNotInArray<FieldName, FieldNames>,
      FieldSchemas
    >,
  ): MultiPageFormBuilder<
    [...FieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    [...UnallocatedFieldNames, EnsureItemNotInArray<FieldName, FieldNames>],
    FieldSchemas
  > {
    this.readonlyFields.push({ ...field, isReadOnly: true });

    return this as never;
  }

  /**
   * Declare the existence of a page in the form.
   * This can only reference fields which have already been declared.
   *
   * @param fields The fields on the page
   * @returns
   */
  withPage<FieldNames extends UnallocatedFieldNames[number][]>(
    fields: EnsureItemsInArray<FieldNames, UnallocatedFieldNames>,
  ): MultiPageFormBuilder<
    FieldNames,
    RemoveItemsFromArray<FieldNames, UnallocatedFieldNames>,
    FieldSchemas
  > {
    this.pages.push({
      fields,
    });

    return this as never;
  }

  /**
   * Add a form-level validation function.
   *
   * @param validationFn A validation function to run on the form as a whole.
   * @returns
   */
  withFormValidation(validationFn: FormValidationFn<FieldSchemas>): this {
    this.validations.push(
      validationFn as FormValidationFn<Record<string, unknown>>,
    );
    return this;
  }

  /**
   * Build the form. Returns "never" if there are any fields unallocated to pages.
   *
   * @returns A schema, initialValues, and pages to be provided to the MultiPageForm component.
   */
  build(props: {
    t: TFunction;
  }): IsEmptyArray<UnallocatedFieldNames> extends true
    ? ({
        incidentWorkItem,
      }: {
        incidentWorkItem?: IncidentWorkItemWithContext;
      }) => {
        strictFieldSchemas: Record<string, ZodType<any>>;
        formSchema: z.ZodType<FieldSchemas>;
        formType: FieldSchemas;
        defaultValues: Partial<FieldSchemas>;
        pages: PageInfoOutput[];
      }
    : never {
    return (({
      incidentWorkItem,
      existingData = {},
    }: {
      incidentWorkItem?: IncidentWorkItemWithContext;
      existingData?: Partial<FieldSchemas>;
    } = {}) => {
      // Validate that all fields have been allocated to a page
      {
        const allFields = [...this.fields, ...this.readonlyFields].map(
          (field): string => field.fieldName as string,
        );

        const fieldsOnPages = this.pages.flatMap(
          (page): readonly unknown[] => page.fields,
        );
        const unallocatedFields = allFields.filter(
          (field) =>
            !fieldsOnPages.includes(field) &&
            // Never-visible fields are exempt from the unallocated check
            this.fields.find((f) => f.fieldName === field)?.type !== "never",
        );

        if (unallocatedFields.length > 0) {
          // The return signature at this point will be "never"
          throw new Error(
            `The following fields have not been allocated to a page: ${unallocatedFields.join(
              ", ",
            )}`,
          );
        }
      }

      // Individual strict schemas for each field.
      // These are used in isolation by Formik to determine whether
      // each provided field is valid.
      const strictFieldSchemas: Record<string, ZodType> = this.fields.reduce(
        (acc, field) => {
          return {
            ...acc,
            [field.fieldName]: field.schema,
          };
        },
        {},
      );

      // A permissive schema which allows any fields to be present.
      const permissiveSchema = createPermissiveSchema(strictFieldSchemas);

      // Clean up any values which should be null because of isRequired.
      //
      // This was previously a chain of one .transform() per field, and every
      // transform re-ran treatInvalidValuesAsUndefined (which safeParses each
      // field), making a single parse O(fields^2). We collapse it into a single
      // transform that performs the identical sequential forward pass in O(fields):
      // a field's visibility is still evaluated against the values with all
      // earlier-hidden fields already nulled out. Because treatInvalidValuesAsUndefined
      // normalizes each field independently, we can update that normalized view
      // incrementally (re-deriving only the field we just nulled) instead of
      // recomputing it for every field.
      const cleanedUpSchema: ZodType = permissiveSchema.transform((data) => {
        // `this.fields` is heterogeneous, so the values bag is typed loosely
        // here — matching the previous per-field transform chain, which passed
        // `data as ZodType<any>` into treatInvalidValuesAsUndefined / isVisible.
        /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
        let result = data as any;
        const formValues = treatInvalidValuesAsUndefined(
          strictFieldSchemas,
          result as ZodType<any>,
        );

        for (const field of this.fields) {
          const fieldName = field.fieldName as string;
          const isVisible =
            typeof field.isVisible === "function"
              ? field.isVisible({ formValues, incidentWorkItem })
              : true;

          if (!isVisible) {
            // eslint-disable-next-line @typescript-eslint/no-misused-spread
            result = { ...result, [fieldName]: null };
            // Keep the normalized view in sync so subsequent fields see this
            // field as hidden, exactly as the previous per-field transform chain
            // did (each transform recomputed the normalized values from the data
            // with this field already nulled). Per-field normalization is
            // independent, so re-deriving only this field is equivalent.
            const reparsed = strictFieldSchemas[fieldName]?.safeParse(null);
            (formValues as any)[fieldName] = reparsed?.success
              ? reparsed.data
              : undefined;
          }
        }

        return result;
        /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
      });

      // Make sure that all fields which are conditionally required are valid
      const fieldValidatedSchema: ZodType = cleanedUpSchema.check((ctx) => {
        const formValues = treatInvalidValuesAsUndefined(
          strictFieldSchemas,
          ctx.value as z.ZodObject,
        );

        for (const field of this.fields) {
          // Is this field visible (e.g. shown to the user for editing)?
          const fieldIsVisible =
            typeof field.isVisible === "function"
              ? field.isVisible({ formValues, incidentWorkItem })
              : true;

          const fieldValue = (ctx.value as Record<string, unknown>)[
            field.fieldName as string
          ];

          // Is this field valid in isolation?
          const fieldValueParsed =
            strictFieldSchemas[field.fieldName as string]?.safeParse(
              fieldValue,
            );

          if (
            fieldIsVisible &&
            fieldValueParsed !== undefined &&
            !fieldValueParsed.success
          ) {
            if (fieldValue === null) {
              const { t } = props;
              const translationKeyAndOptions = buildTranslationInput(
                (field as BaseFormBuilderField<Record<string, unknown>, string>)
                  .requiredLabel ?? { translationKey: tk("common.required") },
                {
                  t,
                  formValues,
                  incidentWorkItem,
                },
              );

              ctx.issues.push({
                code: "custom",
                message: translate(t, translationKeyAndOptions),
                input: fieldValue,
                path: [field.fieldName],
                continue: true,
              });
            } else {
              fieldValueParsed.error.issues.forEach((i) =>
                ctx.issues.push(i as (typeof ctx.issues)[number]),
              );
            }
          }
        }
      });

      // Run any form-level validations
      const validatedSchema: ZodType = this.validations.reduce(
        (acc, validationFn) =>
          acc.check((ctx) => {
            validationFn({
              t: props.t,
              formValues: ctx.value as Record<string, unknown>,
              incidentWorkItem,
            }).forEach(({ fieldName, message }) => {
              ctx.issues.push({
                code: "custom",
                path: [fieldName],
                message,
                fatal: true,
                input: ctx.value,
              });
            });
          }),
        fieldValidatedSchema,
      );

      const pages = this.pages.map((page) => {
        return {
          fields: page.fields.map((fieldName) => {
            const field = [...this.fields, ...this.readonlyFields].find(
              (f) => f.fieldName === fieldName,
            ) as FieldInfo<any, z.ZodType, any> | ReadonlyFieldInfo<any, any>;
            return {
              ...field,
              visibilityFn:
                typeof field.isVisible === "function"
                  ? field.isVisible
                  : undefined,
              initialValueFn:
                "initialValue" in field &&
                typeof field.initialValue === "function"
                  ? field.initialValue
                  : undefined,
              editabilityFn:
                "isEditable" in field && typeof field.isEditable === "function"
                  ? field.isEditable
                  : undefined,
            };
          }),
        };
      });

      // Compute initial values for editable fields, merging with existing data
      const defaultValues: Partial<FieldSchemas> = { ...existingData };
      for (const field of this.fields) {
        if (typeof field.defaultValue === "function") {
          const fallbackValue = field.defaultValue({
            incidentWorkItem,
          }) as FieldSchemas[keyof FieldSchemas] | null;
          if (
            defaultValues[field.fieldName as keyof FieldSchemas] ===
              undefined &&
            fallbackValue !== null
          ) {
            defaultValues[field.fieldName as keyof FieldSchemas] =
              fallbackValue;
          }
        }
      }

      return {
        strictFieldSchemas: strictFieldSchemas,
        formSchema: validatedSchema,
        formType: {} as FieldSchemas,
        defaultValues,
        pages: pages,
      } as unknown as {
        strictFieldSchemas: Record<string, ZodType>;
        formSchema: z.ZodType<FieldSchemas>;
        formType: FieldSchemas;
        defaultValues: Partial<FieldSchemas>;
        pages: PageInfoOutput[];
      };
    }) as unknown as IsEmptyArray<UnallocatedFieldNames> extends true
      ? ({
          incidentWorkItem,
          existingData,
        }: {
          incidentWorkItem?: IncidentWorkItemWithContext;
          existingData?: Partial<FieldSchemas>;
        }) => {
          strictFieldSchemas: Record<string, ZodType>;
          formSchema: z.ZodType<FieldSchemas>;
          formType: FieldSchemas;
          defaultValues: Partial<FieldSchemas>;
          pages: PageInfoOutput[];
        }
      : never;
  }
}
